import React, { useState, useRef } from 'react';
import { 
  Upload,
  Loader2,
  X,
  Smartphone,
  Monitor,
  ArrowRight,
  Image as ImageIcon
} from "lucide-react";
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";

export const UploadModal = ({ open, onOpenChange }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadError, setUploadError] = useState(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(
      file => file.type.startsWith('video/')
    );
    if (files.length > 0) {
      handleFiles(files);
    }
  };

  const handleFiles = (files) => {
    setSelectedFiles(files);
    // Here you would typically start the upload process
    simulateUpload(files);
  };

  const simulateUpload = async (files) => {
    setIsUploading(true);
    setUploadError(null);
    
    try {
      // This is where you would implement the actual upload logic
      // For now, we'll simulate progress
      for (let progress = 0; progress <= 100; progress += 10) {
        setUploadProgress(progress);
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Reset state after "upload"
      setIsUploading(false);
      setUploadProgress(0);
      setSelectedFiles([]);
      onOpenChange(false);
    } catch (error) {
      setUploadError("Failed to upload videos. Please try again.");
      setIsUploading(false);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(
      file => file.type.startsWith('video/')
    );
    if (files.length > 0) {
      handleFiles(files);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl border border-gray-200/50 shadow-xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-foreground bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600">
            Upload Videos
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Desktop Upload Area */}
          <div
            className={`
              relative rounded-xl border-2 border-dashed p-8
              ${isDragging ? 'border-blue-500 bg-blue-50/50 border-opacity-50' : 'border-gray-200/50'}
              transition-colors duration-200 ease-in-out hover:border-blue-500/30 hover:bg-blue-50/30
            `}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="video/*"
              multiple
              onChange={handleFileSelect}
            />
            
            <div className="flex flex-col items-center justify-center space-y-4 text-center">
              <div className="rounded-xl bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 p-4">
                <Monitor className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Drag and drop your videos here
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  or click to select files from your computer
                </p>
              </div>
              <Button
                variant="outline"
                onClick={openFileDialog}
                disabled={isUploading}
                className="relative group overflow-hidden rounded-xl border-gray-200/50 hover:border-transparent transition-all duration-300"
              >
                <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <span className="absolute inset-[1px] bg-white rounded-xl group-hover:bg-opacity-90 transition-colors duration-300" />
                <span className="relative flex items-center gap-2">
                  <Upload className="h-4 w-4 text-purple-600 group-hover:text-purple-700 transition-colors duration-300" />
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 font-medium">
                    Select Files
                  </span>
                </span>
              </Button>
            </div>
          </div>

          {/* Mobile Upload Option */}
          <div className="relative rounded-xl border border-gray-200/50 p-4 hover:border-purple-500/30 hover:bg-purple-50/30 transition-all duration-200">
            <div className="flex items-center space-x-4">
              <div className="rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 p-4">
                <Smartphone className="h-6 w-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900">
                  Upload from your phone
                </h4>
                <p className="text-xs text-gray-500 mt-1">
                  Access your phone's camera or gallery
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-purple-600 hover:bg-purple-50/50 rounded-xl"
                onClick={() => {
                  // Here you would implement mobile upload logic
                  // For example, opening a QR code or sending a link
                }}
              >
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Upload Progress */}
          {isUploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Uploading {selectedFiles.length} videos...</span>
                <span className="text-gray-900 font-medium">{uploadProgress}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Error Message */}
          {uploadError && (
            <div className="text-sm text-red-600 bg-red-50/50 p-4 rounded-xl border border-red-100/50">
              {uploadError}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UploadModal; 